/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {useGetSetting} from '@agent-assist/api-typescript-react-query-hooks';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {useEffect, useState} from 'react';
import {LoadingSpinner} from '../../components/LoadingSpinner';
import {FilterItem, operationOptions} from '../../pages/settings/MyTeamPage';

interface MyTeamFilterModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (members: FilterItem[]) => void;
  initialSelectedFilters?: FilterItem[];
}

// Legacy interface for backward compatibility
interface LegacyFilterConfig {
  teams: FilterItem[];
  users: FilterItem[];
  agents?: FilterItem[];
}

export const MyTeamFilterModal = ({open, onClose, onApply, initialSelectedFilters = []}: MyTeamFilterModalProps) => {
  const [members, setMembers] = useState<FilterItem[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<FilterItem[]>(initialSelectedFilters);
  const [isLoading, setIsLoading] = useState(true);

  // Use the React Query hook to get the settings
  const userSetting = useGetSetting({userKey: 'teamsUsersFilters'});

  useEffect(() => {
    // Load data when userSetting is successful or error
    if (userSetting.isSuccess && userSetting.data?.userValue) {
      try {
        const parsedConfig = JSON.parse(userSetting.data.userValue);

        // Handle backward compatibility with old format
        if (parsedConfig.teams || parsedConfig.users || parsedConfig.agents) {
          const legacyConfig = parsedConfig as LegacyFilterConfig;
          setMembers([...(legacyConfig.teams || []), ...(legacyConfig.users || []), ...(legacyConfig.agents || [])]);
        } else if (parsedConfig.members) {
          setMembers(parsedConfig.members || []);
        } else {
          setMembers([]);
        }
      } catch (error) {
        console.error('Error parsing filter config:', error);
      }
      // Set loading to false after data is loaded
      setIsLoading(false);
    } else if (userSetting.isError) {
      // If setting doesn't exist yet, that's fine
      console.log('No existing team members filters found');
      // Set loading to false even if there's an error
      setIsLoading(false);
    } else if (userSetting.isLoading) {
      // Keep loading state true while fetching data
      setIsLoading(true);
    }
  }, [userSetting.isSuccess, userSetting.data, userSetting.isError, userSetting.isLoading]);

  const isItemSelected = (item: FilterItem) => {
    return selectedFilters.some((filter) => filter.id === item.id);
  };

  const handleItemToggle = (item: FilterItem) => {
    setSelectedFilters((prev) => {
      const isSelected = isItemSelected(item);
      if (isSelected) {
        return prev.filter((filter) => filter.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  const handleClearAll = () => {
    setSelectedFilters([]);
    onApply([]);
    onClose();
  };

  const handleApply = () => {
    onApply(selectedFilters);
    onClose();
  };

  const getOperationLabel = (operation: string): string => {
    return operationOptions.find((option) => option.value === operation)?.label || operation;
  };

  // Check if all items are selected
  const isAllSelected = members.length > 0 && selectedFilters.length === members.length;

  // Check if some but not all items are selected
  const isSomeSelected = selectedFilters.length > 0 && selectedFilters.length < members.length;

  // Handle select all toggle
  const handleSelectAllToggle = () => {
    if (isAllSelected) {
      // If all are selected, clear selection
      setSelectedFilters([]);
    } else {
      // Otherwise select all
      setSelectedFilters([...members]);
    }
  };

  const renderTable = () => (
    <Box sx={{mb: 3}}>
      <TableContainer component={Paper} variant={'outlined'}>
        <Table size={'small'}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Rule</TableCell>
              <TableCell>UserID</TableCell>
              <TableCell align={'center'} width={'80px'}>
                <Checkbox
                  indeterminate={isSomeSelected}
                  checked={isAllSelected}
                  onChange={handleSelectAllToggle}
                  color={'primary'}
                />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} align={'center'}>
                  <Box sx={{py: 2}}>
                    <LoadingSpinner />
                  </Box>
                </TableCell>
              </TableRow>
            ) : members.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align={'center'}>
                  <Typography variant={'body2'} color={'text.secondary'}>
                    No team members configured
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              members.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{getOperationLabel(item.operation)}</TableCell>
                  <TableCell>{item.value}</TableCell>
                  <TableCell align={'center'}>
                    <Checkbox
                      checked={isItemSelected(item)}
                      onChange={() => handleItemToggle(item)}
                      color={'primary'}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth={'md'} fullWidth>
      <DialogTitle>
        My Team
        <IconButton
          aria-label={'close'}
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>{renderTable()}</Stack>
      </DialogContent>
      <DialogActions sx={{px: 3, py: 2, display: 'flex', justifyContent: 'space-between'}}>
        <Button onClick={handleClearAll} color={'inherit'} variant={'outlined'}>
          Clear All
        </Button>
        <Box>
          <Button onClick={onClose} color={'inherit'} sx={{mr: 1}}>
            Cancel
          </Button>
          <Button onClick={handleApply} color={'primary'} variant={'contained'}>
            Apply
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
